export const JS_RESOURCE_CACHE_TARGET = 0.99

export interface ResourceTimingLike {
  name: string
  transferSize: number
  encodedBodySize: number
  decodedBodySize: number
}

export interface JsResourceCacheMetric {
  metricVersion: 1
  resourceScope: 'next_static_js'
  totalResourceCount: number
  requestCount: number
  hitCount: number
  missCount: number
  unknownCount: number
  hitRate: number
  targetHitRate: number
  targetMet: boolean
}

export interface JsResourceCacheRumFields {
  js_resource_cache_metric_version: 1
  js_resource_cache_resource_scope: 'next_static_js'
  js_resource_cache_total_resource_count: number
  js_resource_cache_request_count: number
  js_resource_cache_hit_count: number
  js_resource_cache_miss_count: number
  js_resource_cache_unknown_count: number
  js_resource_cache_hit_rate: number
  js_resource_cache_target_hit_rate: number
  js_resource_cache_target_met: boolean
  js_resource_cache_sw_controlled: boolean
}

const isSameOriginNextJs = (name: string, origin: string): boolean => {
  try {
    const url = new URL(name)
    return (
      url.origin === origin &&
      url.pathname.startsWith('/_next/static/') &&
      url.pathname.endsWith('.js')
    )
  } catch {
    return false
  }
}

export const buildJsResourceCacheMetric = (
  entries: readonly ResourceTimingLike[],
  origin: string,
  targetHitRate = JS_RESOURCE_CACHE_TARGET
): JsResourceCacheMetric => {
  let totalResourceCount = 0
  let hitCount = 0
  let missCount = 0
  let unknownCount = 0

  for (const entry of entries) {
    if (!isSameOriginNextJs(entry.name, origin)) continue
    totalResourceCount += 1

    if (entry.transferSize > 0) {
      missCount += 1
    } else if (entry.encodedBodySize > 0 || entry.decodedBodySize > 0) {
      hitCount += 1
    } else {
      unknownCount += 1
    }
  }

  const requestCount = hitCount + missCount
  const hitRate = requestCount > 0 ? hitCount / requestCount : 0

  return {
    metricVersion: 1,
    resourceScope: 'next_static_js',
    totalResourceCount,
    requestCount,
    hitCount,
    missCount,
    unknownCount,
    hitRate,
    targetHitRate,
    targetMet: requestCount > 0 && hitRate >= targetHitRate,
  }
}

export const collectJsResourceCacheRumFields = (
  entries: readonly ResourceTimingLike[] = performance.getEntriesByType(
    'resource'
  ) as PerformanceResourceTiming[],
  origin = window.location.origin,
  isServiceWorkerControlled = 'serviceWorker' in navigator &&
    navigator.serviceWorker.controller != null
): JsResourceCacheRumFields => {
  const metric = buildJsResourceCacheMetric(entries, origin)

  return {
    js_resource_cache_metric_version: metric.metricVersion,
    js_resource_cache_resource_scope: metric.resourceScope,
    js_resource_cache_total_resource_count: metric.totalResourceCount,
    js_resource_cache_request_count: metric.requestCount,
    js_resource_cache_hit_count: metric.hitCount,
    js_resource_cache_miss_count: metric.missCount,
    js_resource_cache_unknown_count: metric.unknownCount,
    js_resource_cache_hit_rate: metric.hitRate,
    js_resource_cache_target_hit_rate: metric.targetHitRate,
    js_resource_cache_target_met: metric.targetMet,
    js_resource_cache_sw_controlled: isServiceWorkerControlled,
  }
}
